import { Component, ElementRef, NgZone, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthProvider } from './services/auth.providers';
import { SplashScreen } from '@capacitor/splash-screen';
import { Platform } from '@ionic/angular/lazy';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { AppUpdateService } from 'src/app/services/app-update.service';
import { CloudLoggingService } from 'src/app/services/cloud-logging.service';
import { NetworkService } from 'src/app/services/network.service';
import { environment } from 'src/environments/environment';
import { JobPhotosService } from 'src/app/components/job-photos/job-photos.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { isWeb } from '@smartmoving/sm-mobile-shared-lib';
import { register } from 'swiper/element/bundle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import { NavigationTabs } from 'src/app/shared/navigation-tabs';
import { ApiClientService, CurrentUserService, LocalStorageKeys } from '@smartmoving/sm-shared-lib';
import { WarehouseAccessService } from 'src/app/shared/warehouse-access.service';
import { WarehouseService } from 'src/app/pages/warehouse/warehouse.service';
import { RefreshResult } from 'src/generated/core/Authentication/refresh-result';
import { ImpersonationResult } from 'src/app/components/impersonation-details/impersonation-result.interface';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { CREW_ACTION_MODE_CLASS } from 'src/app/shared/action-mode.constants';

register();

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  hideTabs = true;
  showTabUrls = [`/${NavigationTabs.ScheduleView}`, `/${NavigationTabs.JobHistory}`, `/${NavigationTabs.Warehouse}`, `/${NavigationTabs.Settings}`]
  impersonatingCompany = JSON.parse(localStorage.getItem(LocalStorageKeys.IMPERSONATE_COMPANY_KEY)) as ImpersonationResult;

  // Created by Claude Code AI Agent
  // Resolved from a live warehouse-enabled query on app load and after each login (see
  // resolveWarehouseTab) rather than a synchronous getter, so an admin toggling the setting
  // off is reflected on the next refresh without a re-login.
  showWarehouseTab = false;

  // Intentionally inert per product/design: the Warehouse badge is hidden for now.
  // The markup, guard (@if > 0), and styling remain in place, so the badge renders
  // automatically once this reflects a real pending-count source (downstream).
  warehousePendingCount = 0;

  protected readonly NavigationUrls = NavigationTabs;

  constructor(private readonly authProvider: AuthProvider,
              private readonly router: Router,
              private readonly platform: Platform,
              private readonly updater: AppUpdateService,
              private readonly jobPhotosService: JobPhotosService,
              private readonly zone: NgZone,
              private readonly renderer: Renderer2,
              private readonly elementRef: ElementRef,
              private readonly currentUser: CurrentUserService,
              private readonly apiClient: ApiClientService,
              private readonly warehouseAccess: WarehouseAccessService,
              private readonly warehouseService: WarehouseService,
              private readonly cloudLogging: CloudLoggingService,
              private readonly networkService: NetworkService) {
  }

  async ngOnInit() {
    this.setupGainsight();
    await this.initializeApp();
    if (this.impersonatingCompany) {
      this.renderer.addClass(document.body, 'impersonation');
    }
  }

  private async initializeApp() {
    await this.authProvider.initialize();
    this.platform.ready().then(async () => {
      if (!isWeb()) {
        try {
          await FirebaseCrashlytics.setEnabled({ enabled: environment.production });
        } catch (e) {
          console.error('Failed to initialize Firebase Crashlytics', e);
        }
        await CapacitorUpdater.notifyAppReady();
        await StatusBar.setStyle({ style: Style.Light });
      }
      // Added timeout to prevent a blank white screen after splash screen
      setTimeout(() => {
        SplashScreen.hide();
      }, 1000);

      if (!environment.apiUrl.includes('localhost') && !environment.apiUrl.includes('ngrok')) {
        try {
          // Skipped entirely when the device is offline: the manifest fetch would burn six
          // interceptor retries before failing.
          if (await this.networkService.isConnected()) {
            await this.updater.updateNow();
          }
        } catch (e) {
          // Caught here rather than inside AppUpdateService: the manual update check and the
          // environment switch on the login screen rely on updateNow() throwing.
          if (e instanceof HttpErrorResponse && e.status === 0) {
            // No response reached the device at all (offline, DNS or TLS), which is expected on a
            // shift start and is what was flooding support logging.
            console.error('Could not reach the OTA update server', e);
          } else if (!this.updater.alreadyReported(e)) {
            // Apply-stage failures already carry their target version from AppUpdateService.
            await this.cloudLogging.writeErrorObject(e, 'Failed to check for an OTA update');
          }
        }
      }
      await this.jobPhotosService.resetCacheState();

      // See if permissions have changed. If so, let's apply the token change.
      if (this.currentUser.isAuthenticated()) {

        try {
          const result = await this.apiClient.get<RefreshResult>('crew-app/authentication/refresh-token');

          if (result.tokenHasChanged) {
            this.currentUser.updateTokenAndPermissions(result);
          }
        } catch {
          // We don't want to do anything here. If the refresh fails, we'll just let the user re-login.
        }

        await this.resolveWarehouseTab();
      }
    });

    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      await this.zone.run(async () => {
        // to take the path /foo/bar from https://domain.com/#/foo/bar
        const slug = ('/' + event.url.split('/').slice(3).join('/')).replace('/#', '');
        if (slug) {
          await this.router.navigateByUrl(slug);
        }
      });
    });

    this.router.events.pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd)
    ).subscribe(x => {
      const url = (x as NavigationEnd).urlAfterRedirects;
      // Show tabs for exact matches or any settings sub-pages
      this.hideTabs = !this.showTabUrls.some(tabUrl => url === tabUrl || url.startsWith(tabUrl + '/'));
      // Action mode belongs to one page at a time, so a completed navigation always ends it. ActionBarComponent
      // also clears it when it is destroyed; this stays as the shell-level backstop for any route change that
      // does not tear the bar down.
      this.renderer.removeClass(document.body, CREW_ACTION_MODE_CLASS);
    });

    // A login switches the user/company, so drop the cached warehouse-enabled value and re-resolve
    // the tab — this makes it appear right after sign-in without needing a full refresh.
    this.currentUser.currentUserEstablished.pipe(untilDestroyed(this)).subscribe(async () => {
      this.warehouseAccess.reset();
      this.warehouseService.reset();
      await this.resolveWarehouseTab();
    });
  }

  // Created by Claude Code AI Agent
  private async resolveWarehouseTab() {
    try {
      this.showWarehouseTab = await this.warehouseAccess.hasWarehouseAccess();
    } catch {
      // If the live check fails, hide the tab; the route guard enforces access regardless.
      this.showWarehouseTab = false;
    }
  }

  private setupGainsight() {
    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.text = `
      // Gainsight PX
      (function (n, t, a, e, co) {
        var i = "aptrinsic";
        n[i] = n[i] || function () {
          (n[i].q = n[i].q || []).push(arguments)
        }, n[i].p = e;
        n[i].c = co;
        var r = t.createElement("script");
        r.async = !0, r.src = a + "?disableTracking=true&a=" + e;
        var c = t.getElementsByTagName("script")[0];
        c.parentNode.insertBefore(r, c)
      })(window, document, "https://web-sdk.aptrinsic.com/api/aptrinsic.js",
      "${environment.production ? 'AP-NJFSLX5QHIWL-2' : 'AP-NJFSLX5QHIWL-2-3'}");`;

    this.renderer.appendChild(this.elementRef.nativeElement.ownerDocument.body, script);
  }
}
